import React, { useState } from 'react';
import { Book, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { comicsData } from '../data/mock';
import { ShareButton } from './ShareButton';

export const Comics = () => {
  const [selectedComic, setSelectedComic] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const openComicReader = (comic) => {
    setSelectedComic(comic);
    setCurrentPage(0);
  };

  const closeComicReader = () => {
    setSelectedComic(null);
    setCurrentPage(0);
  };

  const nextPage = () => {
    if (selectedComic && currentPage < selectedComic.pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const previousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <>
      {/* Comic Reader Modal */}
      {selectedComic && (
        <div className="fixed inset-0 z-50 bg-gunmetal-950/98 flex items-center justify-center p-4">
          {/* Close Button */}
          <button
            onClick={closeComicReader}
            className="absolute top-6 right-6 z-50 w-12 h-12 bg-gunmetal-800/80 backdrop-blur-sm rounded-full flex items-center justify-center text-steel-300 hover:text-white hover:bg-accent-red transition-all duration-300"
          >
            <X size={24} />
          </button>

          <div className="w-full max-w-5xl">
            {/* Comic Info */}
            <div className="mb-4 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-steel-100 mb-2">{selectedComic.title}</h3>
              <p className="text-steel-400">Page {currentPage + 1} of {selectedComic.pages.length}</p>
            </div>

            {/* Comic Page Image */}
            <div className="relative bg-black rounded-lg overflow-hidden shadow-2xl mb-4">
              <img
                src={selectedComic.pages[currentPage]}
                alt={`Page ${currentPage + 1}`}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-center space-x-4">
              <Button
                onClick={previousPage}
                disabled={currentPage === 0}
                className="bg-gunmetal-800 hover:bg-accent-red text-white disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={24} />
                Previous
              </Button>

              <span className="text-steel-300 font-semibold">
                {currentPage + 1} / {selectedComic.pages.length}
              </span>

              <Button
                onClick={nextPage}
                disabled={currentPage === selectedComic.pages.length - 1}
                className="bg-gunmetal-800 hover:bg-accent-red text-white disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight size={24} />
              </Button>
            </div>
          </div>
        </div>
      )}

      <section id="comics" className="relative py-24 bg-gunmetal-950">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-5 noise-texture"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4">
              <span className="text-platinum animate-chrome-pulse">COMIC </span>
              <span className="text-accent-red">SERIES</span>
            </h2>
            <div className="w-24 h-1 bg-accent-red mx-auto mb-6"></div>
            <p className="text-xl text-steel-400 max-w-3xl mx-auto">
              Edgy, hilarious, and unapologetically bold. Dive into our adult comedy comics.
            </p>
          </div>

          {/* Comics Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {comicsData.map((comic, index) => (
              <div
                key={comic.id}
                className="group bg-gunmetal-900/50 backdrop-blur-sm rounded-lg overflow-hidden border border-steel-700/30 hover:border-accent-red/50 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-accent-red/20 cursor-pointer"
                onClick={() => openComicReader(comic)}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Cover Image */}
                <div className="relative h-96 bg-gradient-to-br from-gunmetal-700 to-gunmetal-900 overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gunmetal-950 via-transparent to-transparent opacity-60"></div>
                  
                  {/* Read Badge */}
                  <div className="absolute top-4 right-4 bg-accent-red/90 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <p className="text-white font-bold text-sm">Click to Read</p>
                  </div>

                  {/* Flagship Badge */}
                  {comic.isFlagship && (
                    <div
                      className="absolute top-4 left-4 bg-amber-400 text-gunmetal-950 px-3 py-1.5 rounded-sm shadow-lg shadow-amber-400/30"
                      data-testid={`flagship-badge-${comic.id}`}
                    >
                      <p className="text-[10px] font-black tracking-[0.2em]">
                        ★ FLAGSHIP · 19K+ VIEWS
                      </p>
                    </div>
                  )}

                  {/* Page Count */}
                  <div className="absolute bottom-4 left-4 bg-gunmetal-900/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <p className="text-steel-300 text-sm font-semibold">
                      <Book size={14} className="inline mr-1" />
                      {comic.pages.length} Pages
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-steel-100 mb-2 group-hover:text-accent-red transition-colors duration-300">
                    {comic.title}
                  </h3>
                  <p className="text-steel-400 text-sm mb-4 line-clamp-3">
                    {comic.description}
                  </p>

                  {/* Category Badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs px-3 py-1 bg-accent-red/20 text-accent-red rounded-full border border-accent-red/30">
                      {comic.category}
                    </span>
                    {comic.issue_number && (
                      <span className="text-steel-500 text-sm">
                        Issue #{comic.issue_number}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Text */}
          <div className="mt-16 text-center">
            <p className="text-steel-400 text-lg mb-4">
              More adult comedy comics coming soon!
            </p>
            <p className="text-steel-500 italic mb-6">
              Content rated for mature audiences - humor, satire, and absurdity abound.
            </p>
            <ShareButton
              sectionId="comics"
              sectionTitle="MINDESIGN Comics"
              description="Chokey & Hackenberry, Daffy Dork, The Lion & The Hummingbird, and more — adult comedy comics from MINDESIGN ENTERPRISES."
            />
          </div>
        </div>
      </section>
    </>
  );
};
